import {
  GridColDef,
  GridColumnVisibilityModel,
  GridPaginationModel,
  GridRowSelectionModel,
  GridSortModel,
  useGridApiRef
} from "@mui/x-data-grid-pro";
import type { GridApiPro } from "@mui/x-data-grid-pro/models/gridApiPro";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useFilters } from "../../../common/hooks/useFilters";
import { useFeatureFlagEnabled } from "../../../common/posthog";
import { useArtifacts } from "../../../data/hooks/useArtifacts";
import { Artifact } from "../../../types/artifact";
import { useSelectedGroups } from "../../auth/hooks/useSelectedGroups";
import { useLayout } from "../../layout/LayoutContext";
import { EMPTY_LENGTH, INITIAL_INDEX } from "../constants/numeric";
import { buildColumns } from "../utils/exploreColumns";
import { useRankedCallsMode, type UseRankedCallsModeResult } from "./useRankedCallsMode";

const DEFAULT_PAGE_SIZE = 500;
const DEFAULT_PAGE = 0;

const DEFAULT_COLUMN_VISIBILITY: GridColumnVisibilityModel = {
  pp_process_decision: false,
  pp_reason_code: false,
  pp_detailed_reasoning: false,
  summary: false,
  sc_summary: false,
  ra_process_decision: false,
  category: false
};

const DEFAULT_SORT_MODEL: GridSortModel = [{ field: "created", sort: "desc" }];
const DEFAULT_PAGINATION: GridPaginationModel = { page: DEFAULT_PAGE, pageSize: DEFAULT_PAGE_SIZE };

export const getRowKey = (row: Artifact): string =>
  `${row.interaction_id}-${row.created}`;

export interface ExploreLayout {
  isDrawerOpen: boolean;
  drawerWidth: number;
}

export interface ExploreFiltersApi {
  filters: ReturnType<typeof useFilters>["filters"];
  updateFilter: ReturnType<typeof useFilters>["updateFilter"];
  updateScoreRange: ReturnType<typeof useFilters>["updateScoreRange"];
  isFilterBarExpanded: boolean;
  setIsFilterBarExpanded: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface ExploreData {
  rows: Artifact[];
  rowCount: number;
  loading: boolean;
  refetch: () => void;
}

export interface ExploreGrid {
  apiRef: React.RefObject<GridApiPro>;
  columns: GridColDef[];
  paginationModel: GridPaginationModel;
  setPaginationModel: React.Dispatch<React.SetStateAction<GridPaginationModel>>;
  sortModel: GridSortModel;
  setSortModel: React.Dispatch<React.SetStateAction<GridSortModel>>;
  rankedCallsSortModel: GridSortModel;
  setRankedCallsSortModel: React.Dispatch<React.SetStateAction<GridSortModel>>;
  effectiveSortModel: GridSortModel;
  effectivePageSize: number;
  columnVisibilityModel: GridColumnVisibilityModel;
  setColumnVisibilityModel: React.Dispatch<React.SetStateAction<GridColumnVisibilityModel>>;
  rowSelectionModel: GridRowSelectionModel;
  setRowSelectionModel: React.Dispatch<React.SetStateAction<GridRowSelectionModel>>;
}

export interface ExploreUiState {
  findBestWorstWizardOpen: boolean;
  setFindBestWorstWizardOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isChatSidebarOpen: boolean;
  setIsChatSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isLensTesterOpen: boolean;
  setIsLensTesterOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedArtifactForTesting: Artifact | null;
  setSelectedArtifactForTesting: React.Dispatch<React.SetStateAction<Artifact | null>>;
}

export interface UseExploreStateResult {
  layout: ExploreLayout;
  filterApi: ExploreFiltersApi;
  rankedCalls: UseRankedCallsModeResult;
  data: ExploreData;
  grid: ExploreGrid;
  ui: ExploreUiState;
  permittedEmails: string[] | null;
  isRagEnabled: boolean;
  handleRefresh: () => void;
  handleOpenLensTester: () => void;
}

const useGridState = (): ExploreGrid & { rawSortModel: GridSortModel; rawPaginationModel: GridPaginationModel } => {
  const apiRef = useGridApiRef();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(DEFAULT_PAGINATION);
  const [sortModel, setSortModel] = useState<GridSortModel>(DEFAULT_SORT_MODEL);
  const [rankedCallsSortModel, setRankedCallsSortModel] = useState<GridSortModel>([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(DEFAULT_COLUMN_VISIBILITY);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  return {
    apiRef,
    columns: [],
    paginationModel, setPaginationModel,
    sortModel, setSortModel,
    rankedCallsSortModel, setRankedCallsSortModel,
    effectiveSortModel: sortModel,
    effectivePageSize: paginationModel.pageSize,
    columnVisibilityModel, setColumnVisibilityModel,
    rowSelectionModel, setRowSelectionModel,
    rawSortModel: sortModel,
    rawPaginationModel: paginationModel
  };
};

const useUiState = (): ExploreUiState => {
  const [findBestWorstWizardOpen, setFindBestWorstWizardOpen] = useState(false);
  const [isChatSidebarOpen, setIsChatSidebarOpen] = useState(false);
  const [isLensTesterOpen, setIsLensTesterOpen] = useState(false);
  const [selectedArtifactForTesting, setSelectedArtifactForTesting] = useState<Artifact | null>(null);

  return {
    findBestWorstWizardOpen, setFindBestWorstWizardOpen,
    isChatSidebarOpen, setIsChatSidebarOpen,
    isLensTesterOpen, setIsLensTesterOpen,
    selectedArtifactForTesting, setSelectedArtifactForTesting
  };
};

interface DataParams {
  paginationModel: GridPaginationModel;
  sortModel: GridSortModel;
  filtersApi: ReturnType<typeof useFilters>;
  permittedEmails: string[] | null;
  rankedCalls: UseRankedCallsModeResult;
}

const useExploreData = ({ paginationModel, sortModel, filtersApi, permittedEmails, rankedCalls }: DataParams): ExploreData => {
  const sortField = sortModel[INITIAL_INDEX]?.field;
  const sortDirection = sortModel[INITIAL_INDEX]?.sort as "asc" | "desc" | undefined;

  const { data: artifactsResult, isFetching: standardLoading, refetch } = useArtifacts(
    {
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      sortField,
      sortDirection,
      filters: filtersApi.getApiFilters(),
      permittedEmails
    },
    { enabled: !rankedCalls.isActive }
  );

  const rows = useMemo(
    () => rankedCalls.isActive ? rankedCalls.rows : (artifactsResult?.data || []),
    [rankedCalls.isActive, rankedCalls.rows, artifactsResult?.data]
  );
  const rowCount = rankedCalls.isActive ? rankedCalls.rowCount : (artifactsResult?.count || EMPTY_LENGTH);
  const loading = rankedCalls.isActive ? rankedCalls.loading : standardLoading;

  return { rows, rowCount, loading, refetch };
};

interface SyncEffectsParams {
  filters: ReturnType<typeof useFilters>["filters"];
  rankedCalls: UseRankedCallsModeResult;
  setColumnVisibilityModel: React.Dispatch<React.SetStateAction<GridColumnVisibilityModel>>;
  setRankedCallsSortModel: React.Dispatch<React.SetStateAction<GridSortModel>>;
}

const useGridSyncEffects = ({ filters, rankedCalls, setColumnVisibilityModel, setRankedCallsSortModel }: SyncEffectsParams): void => {
  useEffect(() => {
    const show = filters.processDecision === "all" || filters.processDecision === "DO_NOT_PROCESS";
    setColumnVisibilityModel(prev => ({ ...prev, pp_reason_code: show, pp_detailed_reasoning: show }));
  }, [filters.processDecision, setColumnVisibilityModel]);

  useEffect(() => {
    if (rankedCalls.isActive && rankedCalls.sortModel.length > INITIAL_INDEX) {
      setRankedCallsSortModel(rankedCalls.sortModel);
    }
  }, [rankedCalls.isActive, rankedCalls.sortModel, setRankedCallsSortModel]);
};

interface BuildOpenLensTesterParams {
  rows: Artifact[];
  rowSelectionModel: GridRowSelectionModel;
  setSelectedArtifactForTesting: React.Dispatch<React.SetStateAction<Artifact | null>>;
  setIsLensTesterOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const buildOpenLensTester = ({
  rows, rowSelectionModel, setSelectedArtifactForTesting, setIsLensTesterOpen
}: BuildOpenLensTesterParams) => (): void => {
  if (rowSelectionModel.length > INITIAL_INDEX) {
    const selectedRow = rows.find((row: Artifact) => rowSelectionModel.includes(getRowKey(row)));
    setSelectedArtifactForTesting(selectedRow || null);
  }
  setIsLensTesterOpen(true);
};

interface BuildGridParams {
  gridState: ReturnType<typeof useGridState>;
  rankedCalls: UseRankedCallsModeResult;
  columns: GridColDef[];
}

const buildExploreGrid = ({ gridState, rankedCalls, columns }: BuildGridParams): ExploreGrid => {
  const effectiveSortModel = rankedCalls.isActive ? gridState.rankedCallsSortModel : gridState.sortModel;
  const effectivePageSize = rankedCalls.isActive ? rankedCalls.pageSize : gridState.paginationModel.pageSize;
  return {
    apiRef: gridState.apiRef,
    columns,
    paginationModel: gridState.paginationModel,
    setPaginationModel: gridState.setPaginationModel,
    sortModel: gridState.sortModel,
    setSortModel: gridState.setSortModel,
    rankedCallsSortModel: gridState.rankedCallsSortModel,
    setRankedCallsSortModel: gridState.setRankedCallsSortModel,
    effectiveSortModel,
    effectivePageSize,
    columnVisibilityModel: gridState.columnVisibilityModel,
    setColumnVisibilityModel: gridState.setColumnVisibilityModel,
    rowSelectionModel: gridState.rowSelectionModel,
    setRowSelectionModel: gridState.setRowSelectionModel
  };
};

const useFilterApi = (filtersApi: ReturnType<typeof useFilters>): ExploreFiltersApi => {
  const [isFilterBarExpanded, setIsFilterBarExpanded] = useState(true);
  return {
    filters: filtersApi.filters,
    updateFilter: filtersApi.updateFilter,
    updateScoreRange: filtersApi.updateScoreRange,
    isFilterBarExpanded,
    setIsFilterBarExpanded
  };
};

const useRefreshHandler = (rankedCalls: UseRankedCallsModeResult, data: ExploreData): (() => void) => {
  const isRankedActive = rankedCalls.isActive;
  const refetchRanked = rankedCalls.refetch;
  const refetchData = data.refetch;
  return useCallback((): void => {
    if (isRankedActive) {
      refetchRanked();
      return;
    }
    refetchData();
  }, [isRankedActive, refetchRanked, refetchData]);
};

export const useExploreState = (): UseExploreStateResult => {
  const { permittedEmails } = useSelectedGroups();
  const layout = useLayout();
  const filtersApi = useFilters();
  const rankedCalls = useRankedCallsMode({ permittedEmails });
  const isRagEnabled = useFeatureFlagEnabled("rag-interface") ?? false;

  const filterApi = useFilterApi(filtersApi);
  const ui = useUiState();
  const gridState = useGridState();

  const data = useExploreData({
    paginationModel: gridState.rawPaginationModel,
    sortModel: gridState.rawSortModel,
    filtersApi,
    permittedEmails,
    rankedCalls
  });

  useGridSyncEffects({
    filters: filtersApi.filters,
    rankedCalls,
    setColumnVisibilityModel: gridState.setColumnVisibilityModel,
    setRankedCallsSortModel: gridState.setRankedCallsSortModel
  });

  const columns: GridColDef[] = useMemo(
    () => buildColumns(rankedCalls.isActive, rankedCalls.rankedCallsParams?.attribute),
    [rankedCalls.isActive, rankedCalls.rankedCallsParams?.attribute]
  );

  const handleRefresh = useRefreshHandler(rankedCalls, data);

  const handleOpenLensTester = buildOpenLensTester({
    rows: data.rows,
    rowSelectionModel: gridState.rowSelectionModel,
    setSelectedArtifactForTesting: ui.setSelectedArtifactForTesting,
    setIsLensTesterOpen: ui.setIsLensTesterOpen
  });

  const grid = buildExploreGrid({ gridState, rankedCalls, columns });

  return {
    layout,
    filterApi,
    rankedCalls,
    data,
    grid,
    ui,
    permittedEmails,
    isRagEnabled,
    handleRefresh,
    handleOpenLensTester
  };
};
